import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { CustomFieldType, VisitsModel } from '@w3lcome/types';
import { Button, CustomFieldInput, Header, Text } from '_/components';
import ChevronDown from '_/components/ChevronDown';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import KeyboardContainer from '_/components/KeyboardContainer';
import { colors } from '_/config/theme';
import { useAuth } from '_/hooks/AuthContext';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useVisit } from '_/hooks/VisitContext';
import { useNextCheckinScreen } from '_/hooks/useNextCheckinScreen';
import { CustomFieldWithError } from '_/interfaces/CustomFieldWithError';
import { AppRoute } from '_/navigation/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';

import CheckVisitorIsForeigner from '_/components/CheckVisitorIsForeigner';
import OCRCameraScreen from '_/components/OCRCameraScreen';
import isTransientError from '_/helpers/isTransientError';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import { liberadoApi } from '_/services/api';
import { TextInput } from 'react-native-gesture-handler';
import formatFieldWhenVisitorIsForeigner from '../helpers/formatFieldWhenVisitorIsForeigner';
import { isFieldVisible } from '../helpers/isFieldVisible';

const { height } = Dimensions.get('window');
const HEIGHT_ITEM = 146;

const CheckinInformationScreen: React.FC = () => {
  const { t } = useTranslation();
  const { customization } = useCustomization();
  const { ipad } = useAuth();
  const scrollViewRef = useRef<ScrollView>(null);
  const { goToNextScreen } = useNextCheckinScreen(AppRoute.CHECKIN_INFORMATION);
  const [isOCRCameraModalVisible, setOCRCameraModalVisible] = useState(false);

  const { selectedOrFallbackVisitFlowType, selectedOrFallbackCustomization } =
    useCentralizedValuesFallback();

  const [customFields, setCustomFields] = useState(
    selectedOrFallbackVisitFlowType?.fields as CustomFieldWithError[]
  );

  const [submitFailed, setSubmitFailed] = useState(false);
  const scrollY = useRef(new Animated.Value(0)).current;
  const [scrollSize, setScrollSize] = useState(0);
  const [focusedField, setFocusedField] = useState<CustomFieldWithError | undefined>();
  const [loadingLiberado, setLoadingLiberado] = useState(false);

  const inputRefs = useRef<React.RefObject<TextInput | null>[]>(
    customFields.map(() => React.createRef<TextInput>())
  );

  const [currentIndex, setCurrentIndex] = useState(1);

  const {
    loading,
    visit,
    setVisit,
    getVisitDataByField,
    setVisitDataByField,
    checkFields,
    resetData,
  } = useVisit();

  const { company } = useCustomization();

  const companyId = company?.id;
  const usedLiberado = company?.liberadoAuthToken;

  const visibleCustomFields = useMemo(
    () => customFields.filter((f) => isFieldVisible(f, customFields, getVisitDataByField)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customFields, visit]
  );

  useEffect(() => {
    if (ipad?.singleQuestionEnabled) return;

    inputRefs.current[currentIndex]?.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!ipad?.singleQuestionEnabled) return;

    const timeoutId = setTimeout(() => {
      inputRefs.current[currentIndex]?.current?.focus?.();
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputRefs, currentIndex]);

  useEffect(() => {
    if (visit?.isRecurrent) {
      const alwaysRequiredInputFields = customFields.filter((field) => field.alwaysRequireInput);

      setCustomFields(alwaysRequiredInputFields);

      const visitData = { ...visit };
      alwaysRequiredInputFields.forEach((field) => {
        const visitFieldType = field.type.toLowerCase() as keyof VisitsModel;

        if (field.id !== 'primaryKey' && field.id !== 'name')
          visitData[visitFieldType] = null as any;

        visitData.customFields = visitData.customFields?.map((cf) => {
          if (cf.customFieldId === field.id) cf.value = '';
          return cf;
        });
      });

      setVisit(visitData);
    }
  }, [visit?.isRecurrent]);

  const verifyNameInLiberado = async (
    cpf: string,
    name: string
  ): Promise<'MATCH' | 'MISMATCH' | 'ERROR'> => {
    setLoadingLiberado(true);

    const maxRetries = 2;
    let attempt = 0;

    try {
      while (true) {
        try {
          const response = await liberadoApi.getItem(cpf, {
            params: {
              companyId,
              name,
            },
            timeout: 5000,
          });
          return response?.match ? 'MATCH' : 'MISMATCH';
        } catch (error) {
          const canRetry = attempt < maxRetries && isTransientError(error);

          if (canRetry) {
            attempt++;
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          }

          return 'ERROR';
        }
      }
    } finally {
      setLoadingLiberado(false);
    }
  };

  async function handleSubmit() {
    const checkedFields = ipad?.singleQuestionEnabled
      ? checkFields([customFields[currentIndex]])
      : checkFields(visibleCustomFields);

    const usedCpfAndName = checkedFields.filter(
      (field) => field.type === 'CPF' || field.type === 'NAME'
    );

    if (checkedFields.some(({ hasError }) => hasError)) {
      if (!ipad?.singleQuestionEnabled) {
        const errorMap = new Map(checkedFields.map((f) => [f.id, f.hasError]));
        setCustomFields(
          customFields.map((f) => ({
            ...f,
            hasError: errorMap.has(f.id) ? errorMap.get(f.id) : false,
          }))
        );
      }
      showMessage({
        message: t('infoScreen.missingFieldsError', {
          defaultValue: 'Alguns campos não foram preenchidos corretamente',
        }),
        backgroundColor: colors.danger,
      });
      scrollViewRef.current?.scrollTo({ animated: true, x: 0, y: 0 });
      setSubmitFailed(true);
      return;
    }

    if (ipad?.singleQuestionEnabled && currentIndex < customFields.length - 1) {
      if (submitFailed) setSubmitFailed(false);
      handleNextPress();
      return;
    }

    if (usedLiberado && usedCpfAndName.length === 2 && visit.cpf && visit.name) {
      const status = await verifyNameInLiberado(visit.cpf, visit.name);

      if (status === 'MISMATCH') {
        showMessage({
          message: t('cpfAndNameNotMatch', {
            defaultValue: 'Nome do usuário não confere com o CPF',
          }),
          backgroundColor: colors.danger,
        });
        return;
      }

      if (status === 'ERROR') {
        showMessage({
          message: t('errorOnVerifyUser', {
            defaultValue: 'Erro ao verificar informações do usuário',
          }),
          backgroundColor: colors.danger,
        });
        return;
      }
    }

    goToNextScreen();
  }

  function handleResetVisit() {
    resetData();
  }

  const keyboardType = useMemo(() => {
    switch (focusedField?.type) {
      case CustomFieldType.PHONE:
      case CustomFieldType.CPF:
        return 'numeric';
    }
    return 'default';
  }, [focusedField]);

  function onSubmitNextInput() {
    if (focusedField) {
      const lastField = ipad?.singleQuestionEnabled
        ? customFields[currentIndex].id
        : customFields[customFields.length - 1].id;

      if (focusedField.id === lastField) {
        handleSubmit();
        return;
      }
      const nextIndexInput = customFields.indexOf(focusedField) + 1;
      if (
        customFields[nextIndexInput]?.type === CustomFieldType.DROPDOWN ||
        focusedField === customFields[customFields.length - 1]
      ) {
        setFocusedField(undefined);
        return;
      }
      inputRefs.current[nextIndexInput]?.current?.focus?.();
    }
  }

  const handleNextPress = () => {
    setCurrentIndex(currentIndex + 1);
  };

  const handleBackPress = () => {
    setCurrentIndex(currentIndex - 1);
  };

  const handleFocus = (index: number) => {
    const scrollView = scrollViewRef.current;
    const input = inputRefs.current[index]?.current;

    if (scrollView && input && input.measureInWindow) {
      input.measureInWindow((x, y, width, height) => {
        const windowHeight = Dimensions.get('window').height;
        const keyboardHeight = Platform.OS === 'ios' ? 300 : 200;

        if (y + height > windowHeight - keyboardHeight) {
          const scrollTo = y + height - (windowHeight - keyboardHeight) + 20;
          scrollView.scrollTo({ y: scrollTo, animated: true });
        }
      });
    } else {
      console.warn('input.measureInWindow não disponível');
    }
  };

  const customField = (field: CustomFieldWithError, index: number) => {
    if (!isFieldVisible(field, customFields, getVisitDataByField)) return null;

    field = formatFieldWhenVisitorIsForeigner(field, visit, t);

    // check if has some Document field before the current field
    if (field.type === CustomFieldType.DOCUMENT) {
      const firstDocumentField = customFields.find(
        (customField) =>
          customField.type === CustomFieldType.RG || customField.type === CustomFieldType.CPF
      );

      const hasDocumentBefore = firstDocumentField?.id !== field.id;

      if (hasDocumentBefore) {
        return null;
      }
    }

    const [checkField] = checkFields([field]);

    return (
      <CustomFieldInput
        key={field.id}
        value={getVisitDataByField(field) as string}
        isDisabled={field.disabled}
        onChangeText={(value) => setVisitDataByField(value, field)}
        customField={submitFailed ? checkField : field}
        countryCode={visit.countryCode}
        ref={inputRefs.current[index]}
        onFocus={() => {
          setFocusedField(field);
          handleFocus(index);
        }}
        onSubmitEditing={onSubmitNextInput}
        onBlur={() => setFocusedField(undefined)}
        onSubmit={handleSubmit}
        setCountryCode={(code) => setVisit({ countryCode: code })}
      />
    );
  };

  const documentTypeField = customFields.find(
    (field) => field.type.toUpperCase() === CustomFieldType.CPF || field.type === CustomFieldType.RG
  );

  return (
    <KeyboardContainer
      keyboardOpen={!!focusedField}
      onSubmit={onSubmitNextInput}
      inputName={focusedField?.name || ''}
      onChange={(value) => setVisitDataByField(value, focusedField)}
      initialValue={getVisitDataByField(focusedField) as string}
      keyboardType={keyboardType}
    >
      <KeyboardAvoidingView
        enabled
        style={StyleSheet.absoluteFill}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <Animated.ScrollView
          ref={scrollViewRef}
          style={styles.container}
          keyboardShouldPersistTaps="always"
          onContentSizeChange={(w, h) => setScrollSize(h)}
          contentContainerStyle={{ paddingBottom: 60 }}
          onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
            useNativeDriver: Platform.OS !== 'web',
          })}
          scrollEventThrottle={16}
        >
          <View
            style={[
              styles.container,
              { alignItems: 'center', paddingBottom: focusedField ? 60 : 0 },
            ]}
          >
            <ContainerWidthLimit>
              <Header onGoBack={handleResetVisit} />
              <ChevronDown
                scrollY={scrollY}
                isVisible={height < scrollSize - HEIGHT_ITEM}
                extraPadding={60}
                scrollSize={scrollSize}
                scrollViewRef={scrollViewRef}
              >
                <View style={styles.content}>
                  {documentTypeField && documentTypeField.id !== 'primaryKey' && (
                    <View style={styles.foreignerOptionContainer}>
                      <CheckVisitorIsForeigner
                        foreignerDocumentSelected={visit.isForeigner ?? false}
                        onClick={() => setVisit({ isForeigner: !visit.isForeigner })}
                        buttonKey={documentTypeField.type}
                      />
                    </View>
                  )}

                  {!ipad?.singleQuestionEnabled &&
                    selectedOrFallbackCustomization?.documentOcrEnabled &&
                    documentTypeField && (
                      <View style={{ width: '50%', marginBottom: 8, marginTop: 32 }}>
                        <Button
                          outline
                          onPress={() => setOCRCameraModalVisible(true)}
                          disabled={loading}
                          buttonStyles={{
                            borderRadius: 8,
                          }}
                          leftIcon={
                            <MaterialCommunityIcons
                              name="line-scan"
                              size={32}
                              style={{ color: colors.dark3, marginRight: 16 }}
                            />
                          }
                        >
                          <Text fontType="medium" size={18} color={colors.dark3}>
                            {t('Escanear documento')}
                          </Text>
                        </Button>
                      </View>
                    )}

                  {ipad?.singleQuestionEnabled ? (
                    <>{customField(customFields[currentIndex], currentIndex)}</>
                  ) : (
                    <>{customFields?.map((field, index) => customField(field, index))}</>
                  )}

                  <View
                    style={{
                      ...styles.buttonsContainer,
                      ...(currentIndex === 1
                        ? {
                            width: '60%',
                          }
                        : { width: '75%' }),
                    }}
                  >
                    {currentIndex !== 1 && (
                      <Button
                        backgroundColor={colors.white}
                        labelColor={customization?.mainColor || colors.primary}
                        loading={loading}
                        containerStyles={{
                          ...styles.buttonContainer,
                        }}
                        buttonStyles={[
                          styles.button,
                          {
                            justifyContent: loading ? 'center' : 'space-between',
                            borderWidth: 2,
                            borderColor: customization?.mainColor || colors.primary,
                          },
                        ]}
                        leftIcon="arrow-left"
                        onPress={handleBackPress}
                      >
                        {t('Back', { defaultValue: 'Voltar' })}
                      </Button>
                    )}

                    <Button
                      backgroundColor={customization?.mainColor || colors.primary}
                      labelColor={customization?.backgroundColor || colors.white}
                      loading={loading || loadingLiberado}
                      containerStyles={{
                        flex: 1,
                      }}
                      buttonStyles={[
                        styles.button,
                        { justifyContent: loading ? 'center' : 'space-between' },
                      ]}
                      rightIcon="arrow-right"
                      onPress={handleSubmit}
                    >
                      {t('Next', { defaultValue: 'Próximo' })}
                    </Button>
                  </View>
                </View>
              </ChevronDown>
            </ContainerWidthLimit>
          </View>
        </Animated.ScrollView>
      </KeyboardAvoidingView>

      {isOCRCameraModalVisible && (
        <OCRCameraScreen
          isVisible={isOCRCameraModalVisible}
          onClose={() => setOCRCameraModalVisible(false)}
          onScan={(data) => setVisit({ ...data, ocrUsed: true })}
          saveOcrPicture={!!selectedOrFallbackCustomization?.documentOcrSaveImageEnabled}
        />
      )}
    </KeyboardContainer>
  );
};

export default CheckinInformationScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  content: {
    alignItems: 'center',
    flex: 1,
  },
  buttonContainer: {
    width: '24%',
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    marginTop: 26,
    marginBottom: 22,
    height: 64,
  },
  foreignerOptionContainer: {
    paddingBottom: 8,
  },
});
